import Chart from "react-apexcharts";
import { useDispatch } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { adminUserActivitySnapshot } from "../../../actions/dashboard_activities";
import {
  Card,
  CardHeader,
  Button,
  UncontrolledButtonDropdown,
  DropdownMenu,
  CardBody,
  DropdownToggle,
  ButtonGroup,
  DropdownItem,
} from "reactstrap";
import styles from "../../../assets/css/rstyling.module.css";
import Loader from "react-loaders";
import LoadingOverlay from "react-loading-overlay-ts";

/* ================= config ================= */

const INTERVALS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  // { value: "month", label: "Month" },
  //{ value: "year", label: "Year" },
];

// Tier 2 options per interval (first item = default when interval changes)
const PERIODS = {
  day: [
    { value: 7, label: "Last 7 days" },
    { value: 15, label: "Last 15 days" },
    { value: 30, label: "Last 30 days" },
  ],
  week: [
    { value: 4, label: "Last 4 weeks" },
    { value: 8, label: "Last 8 weeks" },
    { value: 12, label: "Last 12 weeks" },
  ],
  // month: [
  //   { value: 3, label: "Last 3 months" },
  //   { value: 6, label: "Last 6 months" },
  //   { value: 12, label: "Last 12 months" },
  // ],
  // year: [
  //   { value: 1, label: "Last year" },
  //   { value: 2, label: "Last 2 years" },
  //   { value: 3, label: "Last 3 years" },
  // ],
};

const DEFAULT_INTERVAL = "day";
const DEFAULT_PERIOD = 15;
const MAX_CUSTOM_DAYS = 180; // can be set to null to remove the cap for start date and end date
const MIN_POINT_WIDTH = 70;
const CHART_MIN_WIDTH = 850;
const DAY_MS = 86400000;


const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const fromISO = (s) => new Date(`${s}T00:00:00`);

const fmtDate = (d) =>
  d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};


const resolvePreset = (interval, period) => {
  const end = startOfToday();
  const start = new Date(end);
  if (interval === "day") start.setDate(start.getDate() - period + 1);
  if (interval === "week") start.setDate(start.getDate() - period * 7 + 1);
  if (interval === "month") {
    start.setMonth(start.getMonth() - period);
    start.setDate(start.getDate() + 1);
  }
  if (interval === "year") {
    start.setFullYear(start.getFullYear() - period);
    start.setDate(start.getDate() + 1);
  }
  return { start, end };
};

const buildDefaultFilter = () => {
  const { start, end } = resolvePreset(DEFAULT_INTERVAL, DEFAULT_PERIOD);
  return {
    mode: "preset",
    interval: DEFAULT_INTERVAL,
    period: DEFAULT_PERIOD,
    startDate: toISO(start),
    endDate: toISO(end),
  };
};

// X-axis label format
const formatLabel = (periodStart, interval) => {
  const d = new Date(periodStart);
  if (interval === "year") return String(d.getFullYear());
  if (interval === "month")
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};



const UserSnapshot = () => {
  const dispatch = useDispatch();

  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(false);


  const [appliedFilter, setAppliedFilter] = useState(buildDefaultFilter);


  const today = useMemo(startOfToday, []);
  const defaultCustomStart = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - 14);
    return d;
  }, [today]);

  const [mode, setMode] = useState("preset"); // "preset" | "custom"
  const [interval, setIntervalValue] = useState(DEFAULT_INTERVAL);
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [startDate, setStartDate] = useState(toISO(defaultCustomStart));
  const [endDate, setEndDate] = useState(toISO(today));
  


  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    dispatch(
      adminUserActivitySnapshot({
        interval: appliedFilter.interval,
        startDate: appliedFilter.startDate,
        endDate: appliedFilter.endDate,
      })
    )
      .then((response) => {

        if (!cancelled) setUsersData(response.payload.content || []);
      })
      .catch(() => {
        if (!cancelled) setUsersData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true; // ignore stale responses
    };
  }, [appliedFilter, dispatch]);

  /* ---- filter validation (custom mode) ---- */
  const error = useMemo(() => {
    if (mode !== "custom") return "";
    if (!startDate || !endDate) return "Select a start and end date.";
    const s = fromISO(startDate);
    const e = fromISO(endDate);
    if (s > e) return "Start date must be before the end date.";
    if (e > today) return "End date can't be in the future.";
    const days = Math.round((e - s) / DAY_MS) + 1;
    if (MAX_CUSTOM_DAYS && days > MAX_CUSTOM_DAYS)
      return `Choose a range of ${MAX_CUSTOM_DAYS} days or fewer.`;
    return "";
  }, [mode, startDate, endDate, today]);

  const resolved = useMemo(() => {
    if (mode === "preset") return resolvePreset(interval, Number(period));
    if (error) return null;
    return { start: fromISO(startDate), end: fromISO(endDate) };
  }, [mode, interval, period, startDate, endDate, error]);

  const pointCount = useMemo(() => {
    if (!resolved) return null;
    const days = Math.round((resolved.end - resolved.start) / DAY_MS) + 1;
    if (mode === "custom" || interval === "day") return { n: days, unit: "day" };
    return { n: Number(period), unit: interval };
  }, [resolved, mode, interval, period]);

  const groupedBy = mode === "custom" ? "day" : interval;


  const handleIntervalChange = (e) => {
    const next = e.target.value;
    setIntervalValue(next);
    setPeriod(PERIODS[next][0].value);
  };

  const handleReset = () => {
    setMode("preset");
    setIntervalValue(DEFAULT_INTERVAL);
    setPeriod(DEFAULT_PERIOD);
    setStartDate(toISO(defaultCustomStart));
    setEndDate(toISO(today));
    setAppliedFilter(buildDefaultFilter());
  };

  const handleApply = () => {
    if (error || !resolved) return;
    setAppliedFilter(
      mode === "preset"
        ? {
          mode,
          interval,
          period: Number(period),
          startDate: toISO(resolved.start),
          endDate: toISO(resolved.end),
        }
        : { mode, interval: "day", startDate, endDate }
    );
  };

  /* ---- chart data ---- */
  const xAxisLabels = useMemo(
    () => usersData.map((s) => formatLabel(s.periodStart, appliedFilter.interval)),
    [usersData, appliedFilter.interval]
  );

  const series = useMemo(
    () => [{ name: "Highly Active Users", data: usersData.map((s) => s.highlyActive) },

    ],
    [usersData]
  );

  // Dynamic y-axis instead of a fixed 50-250
  const { yMin, yMax } = useMemo(() => {
    const values = series[0].data;
    if (!values.length) return { yMin: 0, yMax: 100 };
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    const pad = Math.max(5, Math.round((hi - lo) * 0.25));
    return {
      yMin: Math.max(0, Math.floor((lo - pad) / 10) * 10),
      yMax: Math.ceil((hi + pad) / 10) * 10,
    };
  }, [series]);

  const chartWidth = Math.max(CHART_MIN_WIDTH, xAxisLabels.length * MIN_POINT_WIDTH);

  const options = {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true },
      parentHeightOffset: 0,
    },
    colors: ["#3393F3"],
    stroke: { curve: "straight", width: 4, lineCap: "round" },
    markers: {
      size: 5,
      colors: ["#3393F3"],
      strokeColors: "#FFFFFF",
      strokeWidth: 2,
      hover: { size: 6 },
    },
    grid: {
      show: true,
      borderColor: "#E2E2E2",
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 10, bottom: 0, left: 8 },
    },
    xaxis: {
      categories: xAxisLabels,
      axisBorder: { show: true, color: "#9AA6B2", height: 1 },
      axisTicks: { show: true, color: "#9AA6B2", height: 6 },
      labels: {
        show: true,
        hideOverlappingLabels: false,
        trim: false,
        style: { colors: "#333333", fontSize: "12px", fontWeight: 400 },
        offsetY: 2,
      },
      tooltip: { enabled: false },
    },
    yaxis: {
      min: yMin,
      max: yMax,
      tickAmount: 8,
      labels: {
        show: true,
        style: { colors: "#333333", fontSize: "12px", fontWeight: 400 },
        formatter: (value) => Math.round(value),
      },
    },
    tooltip: {
      enabled: true,
      shared: false,
      intersect: true,
      y: { formatter: (value) => `${value.toLocaleString()} users` }
    },

    //        tooltip: {
    //     custom: ({ dataPointIndex }) => {
    //       const point = usersData[dataPointIndex];

    //       return `
    //         <div class="custom-tooltip">
    //           <div class="tooltip-date">${point.date}</div>

    //           <div class="tooltip-row">
    //             <span class="dot"></span>
    //             <span>Highly Active Users:</span>
    //             <strong>${point.highlyActive} users</strong>
    //           </div>

    //           <div class="tooltip-row">
    //             <span class="dot"></span>
    //             <span>Total Users:</span>
    //             <strong>${point.totalUsers} users</strong>
    //           </div>
    //         </div>
    //       `;
    //     },
    //  // },
    //     },
    legend: { show: false },
    dataLabels: { enabled: false },
    fill: { opacity: 1 },
    noData: { text: loading ? "Loading..." : "No data for this range" },
  };



  return (


    <Card className="main-card">
      <CardHeader className="d-flex">
        <h5 className="opacity-6 text-capitalize">Active Users Trend</h5>

      </CardHeader>
      <LoadingOverlay
        tag="div"
        active={loading}
        styles={{
          overlay: (base) => ({
            ...base,
            background: "#fff",
            opacity: 0.5,
          }),
        }}
        spinner={<Loader color="#6c757d" active type="ball-pulse" />}
      >
        <CardBody>





          <div>

            <div className={styles.autf}>
              <div className={styles.autfRow}>

                <div className={styles.autfField}>
                  <span className={styles.autfLabel} id="autf-mode">Filter mode</span>
                  <div className={styles.autfSeg} role="tablist" aria-labelledby="autf-mode">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={mode === "preset"}
                      onClick={() => setMode("preset")}
                    >
                      Quick range
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={mode === "custom"}
                      onClick={() => setMode("custom")}
                    >
                      Custom range
                    </button>
                  </div>
                </div>

                {/* Tier 1: Interval */}
                <div className={styles.autfField}>
                  <label className={styles.autfLabel} htmlFor="autf-interval">Interval</label>
                  {mode === "preset" ? (
                    <select
                      id="autf-interval"
                      className={styles.autfControl}
                      value={interval}
                      onChange={handleIntervalChange}
                    >
                      {INTERVALS.map((i) => (
                        <option key={i.value} value={i.value}>{i.label}</option>
                      ))}
                    </select>
                  ) : (
                    <div className={styles.autfLocked} title="Custom ranges are always grouped by day">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="4" y="11" width="16" height="10" rx="2" />
                        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                      </svg>
                      Day
                    </div>
                  )}
                </div>

                {/* Tier 2: Period or date range */}
                {mode === "preset" ? (
                  <div className={styles.autfField}>
                    <label className={styles.autfLabel} htmlFor="autf-period">Period</label>
                    <select
                      id="autf-period"
                      className={styles.autfControl}
                      value={period}
                      onChange={(e) => setPeriod(Number(e.target.value))}
                    >
                      {PERIODS[interval].map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <>
                    <div className={styles.autfField}>
                      <label className={styles.autfLabel} htmlFor="autf-start">Start date</label>
                      <input
                        id="autf-start"
                        type="date"
                        className={`${styles.autfControl} ${error ? styles.autfControlInvalid : ""}`}
                        value={startDate}
                        max={toISO(today)}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                    <div className={styles.autfArrow} aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </div>
                    <div className={styles.autfField}>
                      <label className={styles.autfLabel} htmlFor="autf-end">End date</label>
                      <input
                        id="autf-end"
                        type="date"
                        className={`${styles.autfControl} ${error ? styles.autfControlInvalid : ""}`}
                        value={endDate}
                        min={startDate || undefined}
                        max={toISO(today)}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  </>
                )}


                <div className={styles.autfActions}>
                  <button type="button" className={styles.autfBtn} onClick={handleReset}>
                    Reset
                  </button>
                  <button type="button" className={`${styles.autfBtn} ${styles.autfBtnPrimary}`} onClick={handleApply}>
                    Apply
                  </button>
                </div>
              </div>

              {error && <p className={styles.autfError} role="alert">{error}</p>}

              {resolved && (
                <div className={styles.autfSummary}>
                  <span>{fmtDate(resolved.start)} – {fmtDate(resolved.end)}</span>
                  <span className={styles.autfBadge}>Grouped by {groupedBy}</span>
                  <span className={styles.autfCount}>
                    {pointCount.n} {pointCount.unit}{pointCount.n > 1 ? "s" : ""} of data
                  </span>
                </div>
              )}
            </div>


            <div
              style={{
                background: "#FFFFFF",
                border: "1px solid #F0F0F0",
                borderRadius: "12px",
                padding: "16px 16px 10px",
                width: "1200px",
                maxWidth: "100%",
                margin: "24px auto 40px",
                boxSizing: "border-box",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
                opacity: loading ? 0.5 : 1,
                transition: "opacity 0.15s",
              }}
            >
              <div
                style={{
                  width: "100%",
                  overflowX: "auto",
                  overflowY: "hidden",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <div style={{ width: `${chartWidth}px`, minWidth: `${chartWidth}px` }}>
                  <Chart
                    options={options}
                    series={series}
                    type="line"
                    height={400}
                    width={chartWidth}
                  />
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </LoadingOverlay>
    </Card>
  );
};

export default UserSnapshot;